import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../../outbox/db'
import { failedSerialsFromRejectedOps } from '../../outbox/useOutbox'

export interface HomeStats {
  /** Serials scanned today, across every status — the operator's own tally. */
  todayCount: number
  /** When the outbox last drained something successfully. Null before the first sync. */
  lastSyncAt: Date | null
  /** The pallet most recently worked on, or null if none this shift. */
  openPalletCode: string | null
  queuedCount: number
  failedCount: number
}

function startOfToday(): number {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
}

/**
 * Every number here is derived from the device's own durable state, so the home screen
 * is fully readable offline. Nothing on this screen waits for the network.
 */
export function useHomeStats(): HomeStats {
  return (
    useLiveQuery(async () => {
      const since = startOfToday()

      const [todayOps, queuedCount, rejectedOps, lastDone, lastPallet] = await Promise.all([
        db.outbox.where('createdAt').aboveOrEqual(since).toArray(),
        db.outbox.where('status').anyOf('queued', 'inflight').count(),
        db.outbox.where('status').equals('rejected').sortBy('seq'),
        db.outbox.orderBy('syncedAt').last(),
        db.recentPallets.orderBy('cachedAt').last(),
      ])

      return {
        // One op can carry several serials, so count serials rather than operations.
        todayCount: todayOps.reduce((sum, op) => sum + op.serialNumbers.length, 0),
        lastSyncAt: lastDone?.syncedAt === undefined ? null : new Date(lastDone.syncedAt),
        openPalletCode: lastPallet?.palletCode ?? null,
        queuedCount,
        failedCount: failedSerialsFromRejectedOps(rejectedOps).length,
      }
    }, []) ?? {
      todayCount: 0,
      lastSyncAt: null,
      openPalletCode: null,
      queuedCount: 0,
      failedCount: 0,
    }
  )
}
